import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Settings, GitBranch, History } from 'lucide-react';
import { FileList, FileViewer } from '../components/Files';
import { CommitHistory } from '../components/Versioning';
import { SharePopup } from '../components/Sharing';
import { WorkspaceSettings } from '../components/Workspace';
import { Spinner } from '../components/Common';
import { BranchSelector } from '../components/Versioning';
import * as api from '../api';
import { WorkspaceFile } from '../types';

export function WorkspacePage() {
  const { name } = useParams<{ name: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const currentPath = searchParams.get('path') || '/';
  const [selectedFile, setSelectedFile] = useState<WorkspaceFile | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [shareFile, setShareFile] = useState<WorkspaceFile | null>(null);
  const [currentBranch, setCurrentBranch] = useState('main');

  const workspaceQuery = useQuery({
    queryKey: ['workspace', name],
    queryFn: () => api.getWorkspace(name!),
    enabled: !!name,
  });

  const filesQuery = useQuery({
    queryKey: ['files', name, currentPath],
    queryFn: () => api.listFiles(name!, currentPath),
    enabled: !!name,
  });

  const commitsQuery = useQuery({
    queryKey: ['commits', name, currentBranch],
    queryFn: () => api.getCommits(name!, currentBranch),
    enabled: !!name && showHistory,
  });

  const fileContentQuery = useQuery({
    queryKey: ['fileContent', name, selectedFile?.path],
    queryFn: () => api.getFileContent(name!, selectedFile!.path),
    enabled: !!name && !!selectedFile && selectedFile.type !== 'directory',
  });

  const branchesQuery = useQuery({
    queryKey: ['branches', name],
    queryFn: () => api.getBranches(name!),
    enabled: !!name,
  });

  const handleNavigate = (path: string) => {
    setSearchParams({ path });
  };

  const handleFileOpen = (file: WorkspaceFile) => {
    if (file.type === 'directory') {
      handleNavigate(file.path);
    } else {
      setSelectedFile(file);
    }
  };

  if (!name) {
    return <div>Workspace not found</div>;
  }

  if (workspaceQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  const workspace = workspaceQuery.data;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-white">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{name}</h1>
            {workspace?.description && (
              <p className="text-sm text-gray-500">{workspace.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <BranchSelector
            branches={branchesQuery.data || ['main']}
            currentBranch={currentBranch}
            onChange={setCurrentBranch}
          />
          <button
            onClick={() => setShowHistory(!showHistory)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
              showHistory
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <History className="w-4 h-4" />
            History
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* File list */}
        <div className={`flex-1 overflow-hidden ${showHistory ? 'w-1/2' : ''}`}>
          <FileList
            workspace={name}
            path={currentPath}
            files={filesQuery.data || []}
            loading={filesQuery.isLoading}
            onNavigate={handleNavigate}
            onFileOpen={handleFileOpen}
            onShare={(file) => setShareFile(file)}
            onViewHistory={() => setShowHistory(true)}
            getCdnUrl={(path) => api.getCdnUrl(name, path)}
          />
        </div>

        {/* History panel */}
        {showHistory && (
          <div className="w-1/2 border-l overflow-hidden">
            <CommitHistory
              commits={commitsQuery.data || []}
              branches={branchesQuery.data || ['main']}
              currentBranch={currentBranch}
              loading={commitsQuery.isLoading}
              onSelectCommit={(commit) => console.log('Selected commit:', commit)}
              onBranchChange={setCurrentBranch}
              onRefresh={() => commitsQuery.refetch()}
            />
          </div>
        )}
      </div>

      {/* File viewer modal */}
      {selectedFile && (
        <FileViewer
          file={selectedFile}
          content={fileContentQuery.data?.content}
          loading={fileContentQuery.isLoading}
          cdnUrl={api.getCdnUrl(name, selectedFile.path)}
          onClose={() => setSelectedFile(null)}
        />
      )}

      {/* Share popup */}
      {shareFile && (
        <SharePopup
          file={shareFile}
          workspace={name}
          cdnUrl={api.getCdnUrl(name, shareFile.path)}
          isOpen={true}
          onClose={() => setShareFile(null)}
        />
      )}

      {/* Settings modal */}
      {workspace && (
        <WorkspaceSettings
          workspace={workspace}
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          onSave={(data) => {
            api.updateWorkspace(name, data);
            setShowSettings(false);
          }}
          onDelete={() => {
            api.deleteWorkspace(name);
            navigate('/');
          }}
        />
      )}
    </div>
  );
}
